/** Inventory for unannotated walking captures. Missing paths remain missing evidence. */
export interface CollectionCaptureMedia {
  dataCollectionOnly?: boolean;
  depthAvailable?: boolean;
  uploadStatus?: string;
  imgCloudPath?: string;
  depthCloudPath?: string;
  captureMetadataCloudPath?: string;
}

export type CollectionCaptureFileKind = "image" | "metadata" | "depth";
export interface CollectionCaptureFile {
  storagePath: string | null;
  zipPath: string | null;
  /** Whether the selected export actually bundles this file. */
  included: boolean;
}
export interface CollectionCaptureRecord {
  captureId: string;
  collectionId: string;
  plotId: string;
  sampleKey: string;
  purpose: "unannotated";
  depthAvailable: boolean;
  status: "complete" | "pending" | "failed" | "missing-files";
  missingFiles: CollectionCaptureFileKind[];
  files: { image: CollectionCaptureFile; metadata: CollectionCaptureFile; depth?: CollectionCaptureFile };
}

export function collectionCaptureRecord(
  identity: Pick<CollectionCaptureRecord, "captureId" | "collectionId" | "plotId" | "sampleKey">,
  media: CollectionCaptureMedia,
  zipPath: (kind: CollectionCaptureFileKind, storagePath: string) => string
): CollectionCaptureRecord {
  const file = (kind: CollectionCaptureFileKind, path?: string): CollectionCaptureFile => ({
    storagePath: path || null,
    zipPath: path ? zipPath(kind, path) : null,
    included: false,
  });
  const depthAvailable = media.depthAvailable ?? Boolean(media.depthCloudPath);
  const files = {
    image: file("image", media.imgCloudPath),
    metadata: file("metadata", media.captureMetadataCloudPath),
    ...(depthAvailable ? { depth: file("depth", media.depthCloudPath) } : {}),
  };
  const missingFiles = (Object.entries(files) as [CollectionCaptureFileKind, CollectionCaptureFile][])
    .filter(([, value]) => !value.storagePath)
    .map(([kind]) => kind);
  const status = ["failed", "retryableFailed", "failedNonRetryable", "partial"].includes(media.uploadStatus ?? "")
    ? "failed"
    : media.uploadStatus !== "uploaded"
      ? "pending"
      : missingFiles.length
        ? "missing-files"
        : "complete";
  return { ...identity, purpose: "unannotated", depthAvailable, status, missingFiles, files };
}

/** Only selected files gate an export; CSV-only remains useful while the phone is syncing. */
export function assertCollectionCapturesExportable(
  captures: readonly CollectionCaptureRecord[],
  selection: { includeOriginalImages: boolean; includeDepthAndMetadata: boolean }
): void {
  if (!selection.includeOriginalImages && !selection.includeDepthAndMetadata) return;
  for (const capture of captures) {
    if (capture.status === "pending" || capture.status === "failed") {
      throw new Error(
        `Capture ${capture.captureId}: ${capture.status}. Collection export is incomplete; finish syncing on the phone and try again.`
      );
    }
    const selectedMissing = capture.missingFiles.filter((kind) =>
      kind === "image" ? selection.includeOriginalImages : selection.includeDepthAndMetadata
    );
    if (selectedMissing.length) {
      throw new Error(
        `Capture ${capture.captureId}: missing ${selectedMissing.join(", ")}. Collection export is incomplete; finish syncing on the phone and try again.`
      );
    }
  }
}

export class RequiredCaptureDownloadError extends Error {
  constructor(captureId: string | undefined, zipPath: string) {
    super(`Capture ${captureId || "unknown"}: export incomplete because a required file could not be downloaded (${zipPath}). Check your connection, ensure the phone has finished uploading, and try again.`);
    this.name = "RequiredCaptureDownloadError";
  }
}

export function requiredCaptureDownloadError(captureId: string | undefined, zipPath: string): RequiredCaptureDownloadError {
  return new RequiredCaptureDownloadError(captureId, zipPath);
}
